import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, useTheme, alpha, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import ReactFlow, { Background, Controls, MiniMap, addEdge, Connection, Edge, Node, OnConnect, useEdgesState, useNodesState, MarkerType, Handle, Position, NodeProps } from 'reactflow';
import 'reactflow/dist/style.css';
import html2canvas from 'html2canvas';

type RoadmapNodeData = {
  title: string;
  description: string;
  startDate?: string;
  targetDate?: string;
};

type RoadmapExport = {
  type: 'release-pilot/roadmap-planner';
  version: string;
  exportedAt: string;
  nodes: Node<RoadmapNodeData>[];
  edges: Edge[];
};

const STORAGE_KEY = 'roadmap-planner-data';
const EXPORT_TYPE: RoadmapExport['type'] = 'release-pilot/roadmap-planner';
const SCHEMA_VERSION = '1.0.0';

const defaultNodeStyle = {
  border: '1px solid rgba(93, 135, 255, 0.35)',
  borderRadius: 12,
  background: '#fff',
  width: 260,
  boxShadow: '0 4px 10px rgba(0,0,0,0.06)'
};

const RoadmapPlanner: React.FC = () => {
  const theme = useTheme();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<RoadmapNodeData>({ title: '', description: '', startDate: '', targetDate: '' });
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<RoadmapNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const onConnect = useCallback<OnConnect>((params: Connection) => {
    setEdges((eds) => addEdge({ ...params, markerEnd: { type: MarkerType.ArrowClosed }, animated: false, style: { stroke: theme.palette.primary.main, strokeWidth: 2 } }, eds));
  }, [setEdges, theme.palette.primary.main]);

  const addNode = useCallback(() => {
    const id = String(Date.now());
    const position = { x: 120 + nodes.length * 40, y: 120 + nodes.length * 40 };
    const data: RoadmapNodeData = { ...form };
    const node: Node<RoadmapNodeData> = { id, position, data, type: 'roadmapNode' };
    setNodes((prev) => [...prev, node]);
    setForm({ title: '', description: '', startDate: '', targetDate: '' });
    setAddOpen(false);
  }, [form, nodes.length, setNodes]);

  const deleteNode = useCallback((id: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  }, [setNodes, setEdges]);

  // Custom node component with handles on all sides for bidirectional connections
  const RoadmapNode: React.FC<NodeProps<RoadmapNodeData>> = ({ data, id }) => {
    return (
      <Box sx={{ ...defaultNodeStyle, position: 'relative' }}>
        {/* Source and target handles on all four sides for bidirectional connections */}
        <Handle type="source" position={Position.Top} id={`${id}-source-top`} style={{ background: '#555' }} />
        <Handle type="target" position={Position.Top} id={`${id}-target-top`} style={{ background: '#555' }} />
        <Handle type="source" position={Position.Right} id={`${id}-source-right`} style={{ background: '#555' }} />
        <Handle type="target" position={Position.Right} id={`${id}-target-right`} style={{ background: '#555' }} />
        <Handle type="source" position={Position.Bottom} id={`${id}-source-bottom`} style={{ background: '#555' }} />
        <Handle type="target" position={Position.Bottom} id={`${id}-target-bottom`} style={{ background: '#555' }} />
        <Handle type="source" position={Position.Left} id={`${id}-source-left`} style={{ background: '#555' }} />
        <Handle type="target" position={Position.Left} id={`${id}-target-left`} style={{ background: '#555' }} />
        
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {data?.title || 'Untitled'}
            </Typography>
            <Tooltip title="Delete node">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteNode(id); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {data?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {data.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
              <Typography variant="caption" sx={{ color: theme.palette.info.main, fontWeight: 600 }}>
                {data?.startDate ? `Start: ${data.startDate}` : 'Start: -'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EventIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
              <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                {data?.targetDate ? `Target: ${data.targetDate}` : 'Target: -'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.nodes && Array.isArray(parsed.nodes)) {
          setNodes(parsed.nodes);
        }
        if (parsed.edges && Array.isArray(parsed.edges)) {
          setEdges(parsed.edges);
        }
      }
    } catch (error) {
      console.error('Failed to load roadmap data from localStorage:', error);
    } finally {
      setIsInitialLoad(false);
    }
  }, [setNodes, setEdges]);

  // Save to localStorage whenever nodes or edges change (but not during initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      try {
        if (nodes.length > 0 || edges.length > 0) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ nodes, edges }));
        } else {
          // Clear localStorage when all nodes/edges are removed
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to save roadmap data to localStorage:', error);
      }
    }
  }, [nodes, edges, isInitialLoad]);

  // Export as image
  const exportAsImage = useCallback(async () => {
    if (!containerRef.current) return;
    
    try {
      // Hide controls temporarily for cleaner export
      const controls = containerRef.current.querySelector('.react-flow__controls');
      const minimap = containerRef.current.querySelector('.react-flow__minimap');
      if (controls) (controls as HTMLElement).style.display = 'none';
      if (minimap) (minimap as HTMLElement).style.display = 'none';

      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#ffffff',
        useCORS: true,
        scale: 2,
        logging: false,
      });

      // Restore controls
      if (controls) (controls as HTMLElement).style.display = '';
      if (minimap) (minimap as HTMLElement).style.display = '';

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `roadmap-${new Date().toISOString().split('T')[0]}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Failed to export image:', error);
    }
  }, []);

  // Export as JSON
  const exportAsJson = useCallback(() => {
    const payload: RoadmapExport = {
      type: EXPORT_TYPE,
      version: SCHEMA_VERSION,
      exportedAt: new Date().toISOString(),
      nodes,
      edges,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roadmap-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [nodes, edges]);

  // Import from JSON file
  const onImportJsonClick = useCallback(() => fileInputRef.current?.click(), []);

  const validateAndApplyImport = useCallback((data: any) => {
    if (!data || typeof data !== 'object') throw new Error('Invalid JSON');
    if (data.type !== EXPORT_TYPE) throw new Error('Unsupported type');
    if (typeof data.version !== 'string') throw new Error('Missing version');
    if (!Array.isArray(data.nodes) || !Array.isArray(data.edges)) throw new Error('Missing nodes/edges');
    // Basic shape checks for nodes
    const okNodes = data.nodes.every((n: any) => n && typeof n.id === 'string' && n.position && typeof n.position.x === 'number' && typeof n.position.y === 'number');
    const okEdges = data.edges.every((e: any) => e && typeof e.source === 'string' && typeof e.target === 'string');
    if (!okNodes || !okEdges) throw new Error('Malformed nodes or edges');
    setNodes(data.nodes);
    setEdges(data.edges);
  }, [setNodes, setEdges]);

  const onImportFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      validateAndApplyImport(json);
    } catch (err) {
      console.error('Failed to import JSON:', err);
      alert('Import failed: ' + (err as Error).message);
    }
  }, [validateAndApplyImport]);

  const nodeTypes = useMemo(() => ({
    roadmapNode: RoadmapNode,
  }), [RoadmapNode]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body1">
          <b>Tip:</b> Drag nodes to reposition. Drag from a node handle to connect steps.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <input ref={fileInputRef} type="file" accept="application/json" style={{ display: 'none' }} onChange={onImportFileChange} />
          <Button variant="outlined" onClick={onImportJsonClick}>
            Import JSON
          </Button>
          <Button variant="outlined" onClick={exportAsJson}>
            Export JSON
          </Button>
          <Button variant="outlined" startIcon={<ImageIcon />} onClick={exportAsImage}>
            Export Image
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Node
          </Button>
        </Box>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          height: '70vh',
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`
        }}
      >
        <ReactFlow
          nodes={nodes.map((n) => ({
            ...n,
            // Ensure all nodes use the custom type
            type: n.type || 'roadmapNode',
          }))}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background gap={16} color={alpha(theme.palette.text.primary, 0.08)} />
          <MiniMap pannable zoomable />
          <Controls position="bottom-right" />
        </ReactFlow>
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Roadmap Node</DialogTitle>
        <DialogContent>
          <TextField label="Title" fullWidth margin="normal" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextField label="Description" fullWidth margin="normal" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <TextField label="Start Date" type="date" fullWidth margin="normal" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          <TextField label="Target Date" type="date" fullWidth margin="normal" InputLabelProps={{ shrink: true }} value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.title.trim()} onClick={addNode}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoadmapPlanner;


